/* motion.js: the v2 motion system's run-time half (motion lane, QA round 7, Oct 6). Spec: scratchpad qa3/motion/MOTION.md.
   Loads after site.js (reads window.ML) and before the builders' scripts. No libraries. With reduced motion on it
   does nothing at all, and the page is the board at rest. What it does:
   1. html.ml-js; tags what sits below the first screen to settle in (.ml-io, then .ml-in in view, IntersectionObserver)
   2. fades photos in as they load (.ml-img, then .is-in on load or error; images already loaded are never touched)
   3. inlines the hand-drawn kit drawings (assets/art-*.svg) and draws their strokes in (stroke-dashoffset);
      a failed or slow fetch falls back to a clip-path reveal on the img (.ml-art-clip)
   4. statement underlines and the footer bar draw when they arrive (.ml-pend, then .ml-in)
   5. a MutationObserver tags swapped markup (grids, cart lines, search results) and plays .ml-open when a
      disclosure loses [hidden]; the cart count bumps on 'ml:cart-updated'; brand page filters resettle the grid
   Other lanes may call ML.motion.tag(el) after injecting markup; nothing depends on it. */
(function () {
  'use strict';
  var ML = window.ML || {};
  var reduce = ML.reduce || (window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null);
  if (reduce && reduce.matches) return;
  var root = document.documentElement;
  if (!('IntersectionObserver' in window) || !('MutationObserver' in window) || !window.requestAnimationFrame || !root.closest) {
    root.classList.add('ml-off');
    return;
  }
  root.classList.add('ml-js');

  var EASE = 'cubic-bezier(.22,.61,.36,1)';
  var NS = 'http://www.w3.org/2000/svg', XL = 'http://www.w3.org/1999/xlink';
  var SETTLE = '.card,.tile,.ftile,.ctile,.review,.visit,.cstory-in,.dcard,.dgph,.s4d-c,.s4p,.help,.ig-app .instafeed-new-layout-item';
  var NO_SETTLE = '.hdr,.drawer,.ovl,.mpanel,.pdp,.osm,.qchm,.sbar,[aria-live]';
  var IMGS = ['.well', '.tile', '.gwell', '.gth', '.cwell', '.dhead-ph', '.phead-ph', '.sph', '.dgph',
    '.mphoto', '.sx-pprod .w', '.ig-app .instafeed-new-layout-item'].map(function (s) { return s + ' img'; }).join(',');
  var ART = '.hp-art,.ftile-art,.rv-art,.ctile-art,.phead-art .pa-row img,.dr-row img,.s4s-d,.nf-art .nf-d';
  var SWAP_SETTLE = '.card,.ig-app .instafeed-new-layout-item';
  var PEND = '.stmt,.ftr';
  var OPEN = '.acc-b,.dblock,.dsubs,.sws-x,.stock,.lead';
  var slice = function (l) { return Array.prototype.slice.call(l); };
  var raf = function (fn) { return window.requestAnimationFrame(fn); };

  function all(scope, sel) {
    var out = scope.querySelectorAll ? slice(scope.querySelectorAll(sel)) : [];
    if (scope.nodeType === 1 && scope.matches(sel)) out.unshift(scope);
    return out;
  }
  function restart(el, cls) { el.classList.remove(cls); void el.offsetWidth; el.classList.add(cls); }

  /* ---------- observers ---------- */
  var settleIO = new IntersectionObserver(function (ents) {
    var n = 0;
    ents.forEach(function (en) {
      if (!en.isIntersecting) return;
      var el = en.target;
      settleIO.unobserve(el);
      el.style.setProperty('--i', Math.min(n++, el.classList.contains('ml-re') ? 8 : 6));
      el.classList.add('ml-in');
    });
  }, { rootMargin: '0px 0px -40px 0px' });
  var pendIO = new IntersectionObserver(function (ents) {
    ents.forEach(function (en) {
      if (!en.isIntersecting) return;
      pendIO.unobserve(en.target);
      en.target.classList.remove('ml-pend');
      en.target.classList.add('ml-in');
    });
  }, { rootMargin: '0px 0px -40px 0px' });
  var artIO = new IntersectionObserver(function (ents) {
    ents.forEach(function (en) {
      if (!en.isIntersecting) return;
      artIO.unobserve(en.target);
      start(en.target);
    });
  }, { rootMargin: '0px 0px -10% 0px' });

  /* a settled element goes back to plain markup, so hover lifts never wait on the settle's delay */
  document.addEventListener('transitionend', function (e) {
    var el = e.target;
    if (e.propertyName !== 'transform' || !el.classList || !el.classList.contains('ml-in') || !el.classList.contains('ml-io')) return;
    el.classList.remove('ml-io', 'ml-in', 'ml-re');
    el.style.removeProperty('--i');
  });
  document.addEventListener('animationend', function (e) {
    var el = e.target;
    if (!el.classList) return;
    ['ml-bump', 'ml-open', 'ml-swap', 'ml-tick'].forEach(function (c) { if (e.animationName === c) el.classList.remove(c); });
    if (el.classList.contains('ml-new') && (e.animationName === 'ml-fade-up' || e.animationName === 'ml-track')) el.classList.remove('ml-new');
  });

  /* ---------- 6, 26. photos fade in as they load; the main photo dips when it changes ---------- */
  function onImg(e) {
    var t = e.target;
    if (!t || t.tagName !== 'IMG') return;
    if (t.classList.contains('ml-img') && !t.classList.contains('is-in')) t.classList.add('is-in');
    else if (e.type === 'load' && t.matches('.gwell img')) restart(t, 'ml-swap');
  }
  document.addEventListener('load', onImg, true);
  document.addEventListener('error', onImg, true);

  /* ---------- 3. drawings ---------- */
  var texts = {}, k = 0, drawing = [];
  function fetchSvg(url) {
    if (!texts[url]) {
      texts[url] = fetch(url, { credentials: 'same-origin' }).then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      });
      texts[url].catch(function () { /* handled where it is used */ });
    }
    return texts[url];
  }
  function build(img, txt) {
    var p = 'ml' + (++k) + '-';
    txt = txt.replace(/\sid="/g, ' id="' + p).replace(/url\(#/g, 'url(#' + p).replace(/href="#/g, 'href="#' + p);
    var doc = new DOMParser().parseFromString(txt, 'image/svg+xml');
    var s = doc.documentElement;
    if (!s || s.localName !== 'svg' || s.getElementsByTagName('parsererror').length) return null;
    /* the strokes sit in <defs> behind one <use>: bring them into the drawing so each path can be styled */
    slice(s.getElementsByTagNameNS(NS, 'use')).forEach(function (u) {
      var ref = (u.getAttribute('href') || u.getAttributeNS(XL, 'href') || '').slice(1);
      var src = ref && s.querySelector('[id="' + ref + '"]');
      if (!src) return;
      var g = doc.createElementNS(NS, 'g');
      if (u.hasAttribute('transform')) g.setAttribute('transform', u.getAttribute('transform'));
      g.appendChild(src);
      u.parentNode.replaceChild(g, u);
    });
    var svg = document.importNode(s, true);
    var cls = (img.getAttribute('class') || '').replace(/\bml-[\w-]+/g, '').replace(/\s+/g, ' ').trim();
    svg.setAttribute('class', (cls ? cls + ' ' : '') + 'ml-svg');
    ['width', 'height', 'style'].forEach(function (a) { if (img.hasAttribute(a)) svg.setAttribute(a, img.getAttribute(a)); });
    if (img.closest('.pa-row,.dr-row')) svg.setAttribute('preserveAspectRatio', 'xMidYMax meet');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    return svg;
  }
  function clean(p) { p.style.removeProperty('stroke-dasharray'); p.style.removeProperty('stroke-dashoffset'); p.style.removeProperty('transition'); }
  /* path i of n starts at i/n of 60% of T and runs 45% of T: line after line, with overlap, as a hand draws */
  function draw(svg, T, delay) {
    var ps = slice(svg.querySelectorAll('path')).filter(function (p) { return !p.closest('defs'); });
    var n = ps.length || 1;
    var lens = ps.map(function (p) { try { return p.getTotalLength(); } catch (e) { return 0; } });
    ps.forEach(function (p, i) {
      var L = Math.ceil(lens[i]) + 1;
      p.style.strokeDasharray = L + ' ' + (L + 10);
      p.style.strokeDashoffset = String(L + 5);
    });
    drawing = drawing.concat(ps);
    svg.getBoundingClientRect();
    raf(function () {
      ps.forEach(function (p, i) {
        p.style.transition = 'stroke-dashoffset ' + Math.round(T * 0.45) + 'ms ' + EASE + ' ' + Math.round(delay + i / n * T * 0.6) + 'ms';
        p.style.strokeDashoffset = '0';
      });
      setTimeout(function () {
        ps.forEach(clean);
        drawing = drawing.filter(function (p) { return ps.indexOf(p) < 0; });
      }, delay + T * 1.05 + 150);
    });
  }
  function fallback(img) { img.classList.remove('ml-art-wait'); img.classList.add('ml-art-clip'); }
  function start(img) {
    if (!img.parentNode) return;
    var T = img.classList.contains('hp-art') ? 1100 : 900;
    /* a row of drawings (collection head, Since 1986 tile) or Our story's shelves draws one after another */
    var row = img.closest('.pa-row,.dr-row');
    var shelf = !row && img.classList.contains('s4s-d') && img.closest('.s4s-sh');
    var delay = row ? 200 + 120 * slice(row.children).indexOf(img) : shelf ? 200 + 120 * slice(shelf.parentNode.querySelectorAll('.s4s-sh')).indexOf(shelf) : 0;
    /* hero: the drawing starts as its panel's cover is half gone, so the first lines are not drawn under it */
    var hp = img.classList.contains('hp-art') && img.closest('.hp');
    var wipe = hp && hp.getAnimations ? hp.getAnimations({ subtree: true }).filter(function (a) { return a.animationName === 'ml-wipe'; })[0] : null;
    if (wipe && wipe.currentTime !== null) {
      var tm = wipe.effect.getComputedTiming();
      delay = Math.max(0, Math.round(tm.delay + tm.duration * 0.5 - wipe.currentTime));
    }
    var due = Date.now() + delay, late = false;
    img.classList.add('ml-art-wait');
    var timer = setTimeout(function () { late = true; fallback(img); }, 1500);
    fetchSvg(img.getAttribute('src')).then(function (txt) {
      if (late || !img.parentNode) return;
      clearTimeout(timer);
      var svg = build(img, txt);
      if (!svg) { fallback(img); return; }
      img.parentNode.replaceChild(svg, img);
      draw(svg, T, Math.max(0, due - Date.now()));
    }, function () {
      if (late) return;
      clearTimeout(timer);
      fallback(img);
    });
  }

  /* ---------- tagging: reads first, then writes, so a long grid never forces layout per card ---------- */
  function tag(scope, swap) {
    var vh = window.innerHeight || root.clientHeight;
    var imgs = all(scope, IMGS).filter(function (im) {
      return !im.complete && !im.classList.contains('ml-img') && (im.getAttribute('src') || '').indexOf('/art-') < 0;
    });
    var arts = all(scope, ART).filter(function (im) { return im.tagName === 'IMG' && !im.classList.contains('ml-take'); });
    var settles = all(scope, swap ? SWAP_SETTLE : SETTLE).filter(function (el) {
      return !el.classList.contains('ml-io') && !el.closest(NO_SETTLE) && !(el.parentElement && el.parentElement.closest('.ml-io'));
    });
    var pends = all(scope, PEND).filter(function (el) { return !el.classList.contains('ml-pend') && !el.classList.contains('ml-in'); });
    var rA = arts.map(function (el) { return el.getBoundingClientRect(); });
    var rS = settles.map(function (el) { return el.getBoundingClientRect(); });
    var rP = pends.map(function (el) { return el.getBoundingClientRect(); });

    imgs.forEach(function (im) { im.classList.add('ml-img'); });
    arts.forEach(function (im, i) {
      var r = rA[i];
      im.classList.add('ml-take');
      if (swap) return;                           /* a re-rendered head keeps its drawing still */
      if (!r.width && !r.height) return;          /* not rendered at this width: leave the img as it is */
      if (r.bottom <= 0) return;                  /* already scrolled past: at rest */
      fetchSvg(im.getAttribute('src'));           /* fetch early, draw when seen */
      if (r.top < vh) start(im);
      else { im.classList.add('ml-art-wait'); artIO.observe(im); }
    });
    settles.forEach(function (el, i) {
      var r = rS[i];
      if (!r.width && !r.height) return;
      if (swap) el.classList.add('ml-io', 'ml-re');      /* swapped in by a click: settle even in view */
      else if (r.top >= vh) el.classList.add('ml-io');    /* below the first screen; the first screen is at rest */
      else return;
      settleIO.observe(el);
    });
    pends.forEach(function (el, i) {
      var r = rP[i];
      if (swap) { el.classList.add('ml-rest'); return; }
      if (r.bottom <= 0) return;
      if (el.classList.contains('ftr') && r.top >= vh) el.classList.add('ml-below');
      el.classList.add('ml-pend');
      pendIO.observe(el);
    });
  }
  tag(document, false);

  /* ---------- swapped and appended markup; disclosures shown later ---------- */
  new MutationObserver(function (list) {
    list.forEach(function (m) {
      if (m.type === 'attributes') {
        var el = m.target;
        if (m.oldValue !== null && !el.hidden && el.matches(OPEN)) restart(el, 'ml-open');
        return;
      }
      slice(m.addedNodes).forEach(function (n) {
        if (n.nodeType !== 1 || n.classList.contains('ml-svg')) return;
        tag(n, true);
        all(n, '.cline,.cart-track').forEach(function (c) { if (!c.closest('.cartm')) c.classList.add('ml-new'); });
      });
    });
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'], attributeOldValue: true });

  slice(document.querySelectorAll('.gcount')).forEach(function (g) {
    new MutationObserver(function () { restart(g, 'ml-tick'); }).observe(g, { childList: true, characterData: true, subtree: true });
  });

  /* ---------- 25. brand page filter, sort and Show more: the cards that remain settle again ---------- */
  function resettle(e) {
    if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
    var t = e.target.closest && e.target.closest('[data-chips] .chip,.tile[data-f],.srtm-o,[data-more-btn]');
    var grid = t && document.getElementById('products-grid');
    if (!grid) return;
    raf(function () {
      raf(function () {
        var cards = slice(grid.querySelectorAll('.card:not(.is-out)'));
        var shown = cards.filter(function (c) { return c.getClientRects().length > 0; });
        shown.forEach(function (c) {
          settleIO.unobserve(c);
          c.classList.remove('ml-in');
          c.classList.add('ml-io', 'ml-re');
          settleIO.observe(c);
        });
      });
    });
  }
  document.addEventListener('click', resettle);
  document.addEventListener('keydown', resettle);

  /* ---------- 33, 36. add to cart receipt: the header count bumps when the number changes ---------- */
  var cc = document.querySelector('[data-cart-count]');
  var last = cc && /\d+/.test(cc.textContent) ? +cc.textContent.match(/\d+/)[0] : null;
  document.addEventListener('ml:cart-updated', function (e) {
    var n = e.detail && typeof e.detail.item_count === 'number' ? e.detail.item_count : null;
    if (n === null || last === null || n === last) { if (n !== null) last = n; return; }
    last = n;
    slice(document.querySelectorAll('[data-cart-count],[data-cart-icon]')).forEach(function (el) { restart(el, 'ml-bump'); });
  });
  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('button[data-qadd]');
    if (b && !b.disabled) restart(b, 'ml-bump');
  });

  /* reduce switched on mid-visit: finish every drawing now (motion.css stops applying by itself) */
  function finish() { drawing.forEach(clean); drawing = []; }
  if (reduce && reduce.addEventListener) reduce.addEventListener('change', function () { if (reduce.matches) finish(); });

  ML.motion = { tag: function (el) { tag(el || document, true); }, drawIn: start };
})();
